// Static configuration + helpers for the Bookings page chip filters and
// timeline glance cards. Pure data — no React, easy to unit-test in future.
//
// Two filter dimensions: Status (workflow state) and Source (booking origin).
// Both share the same chip shape so the rendering component can be generic.

import {
  Layers, CreditCard, CheckCircle2, Ban, AlertTriangle,
  Globe, UserCog, Lock, ListOrdered, Unlock,
} from 'lucide-react';

// ---------- STATUS ----------
export const STATUS_CHIPS = [
  {
    value: 'AWAITING_PAYMENT', label: 'Awaiting Payment', icon: CreditCard,
    activeBg: 'bg-amber-500 text-white border-amber-500',
    inactiveBg: 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100',
    cardAccent: 'border-l-4 border-l-amber-400',
    countBadgeOnActive: 'bg-white/20 text-white',
    countBadge: 'bg-amber-600 text-white',
  },
  {
    value: 'CONFIRMED', label: 'Confirmed', icon: CheckCircle2,
    activeBg: 'bg-emerald-600 text-white border-emerald-600',
    inactiveBg: 'bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100',
    cardAccent: 'border-l-4 border-l-emerald-500',
    countBadgeOnActive: 'bg-white/20 text-white',
    countBadge: 'bg-emerald-700 text-white',
  },
  {
    value: 'CANCELLED', label: 'Cancelled', icon: Ban,
    activeBg: 'bg-gray-700 text-white border-gray-700',
    inactiveBg: 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200',
    cardAccent: 'border-l-4 border-l-gray-400',
    countBadgeOnActive: 'bg-white/20 text-white',
    countBadge: 'bg-gray-700 text-white',
  },
  {
    value: 'PAYMENT_FAILED', label: 'Payment Failed', icon: AlertTriangle,
    activeBg: 'bg-red-600 text-white border-red-600',
    inactiveBg: 'bg-red-50 text-red-800 border-red-200 hover:bg-red-100',
    cardAccent: 'border-l-4 border-l-red-500',
    countBadgeOnActive: 'bg-white/20 text-white',
    countBadge: 'bg-red-700 text-white',
  },
  {
    value: 'RELEASED', label: 'Released', icon: Unlock,
    activeBg: 'bg-sky-600 text-white border-sky-600',
    inactiveBg: 'bg-sky-50 text-sky-800 border-sky-200 hover:bg-sky-100',
    cardAccent: 'border-l-4 border-l-sky-500',
    countBadgeOnActive: 'bg-white/20 text-white',
    countBadge: 'bg-sky-700 text-white',
  },
  {
    value: 'ALL', label: 'All Statuses', icon: Layers,
    activeBg: 'bg-slate-900 text-white border-slate-900',
    inactiveBg: 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50',
    cardAccent: 'border-l-4 border-l-slate-300',
    countBadgeOnActive: 'bg-white/20 text-white',
    countBadge: 'bg-slate-700 text-white',
  },
];

// Fallback chip for any status not in STATUS_CHIPS (e.g. a new status
// value added on the backend before the frontend catches up) — NEVER
// fall back to STATUS_CHIPS[length-1] ("All Statuses"); that chip is a
// filter-bar-only concept and showing its label on a booking card's own
// status badge is misleading (this exact bug happened with "RELEASED"
// before it got its own entry above).
const UNKNOWN_STATUS_CHIP = {
  value: 'UNKNOWN', label: 'Unknown', icon: Layers,
  activeBg: 'bg-slate-700 text-white border-slate-700',
  inactiveBg: 'bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-200',
  cardAccent: 'border-l-4 border-l-slate-300',
  countBadgeOnActive: 'bg-white/20 text-white',
  countBadge: 'bg-slate-700 text-white',
};

export const getChipConfig = (status) =>
  STATUS_CHIPS.find(c => c.value === status) || { ...UNKNOWN_STATUS_CHIP, label: status || 'Unknown' };

// ---------- SOURCE ----------
export const normaliseSource = (booking) => {
  const s = (booking?.source || '').toUpperCase();
  if (s === 'STAFF_BLOCK') return 'STAFF_BLOCK';
  if (s === 'MANUAL_STAFF') return 'MANUAL_STAFF';
  if (s === 'WAITLIST_CONVERSION') return 'WAITLIST_CONVERSION';
  return 'CUSTOMER';
};

export const SOURCE_CHIPS = [
  {
    value: 'ALL', label: 'All Sources', icon: Layers,
    activeBg: 'bg-slate-900 text-white border-slate-900',
    inactiveBg: 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50',
    countBadgeOnActive: 'bg-white/20 text-white',
    countBadge: 'bg-slate-700 text-white',
    cardAccent: 'border-l-4 border-l-slate-300',
    pillBg: 'bg-slate-100 text-slate-700 border border-slate-200',
  },
  {
    value: 'CUSTOMER', label: 'Online', icon: Globe,
    activeBg: 'bg-emerald-600 text-white border-emerald-600',
    inactiveBg: 'bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100',
    countBadgeOnActive: 'bg-white/20 text-white',
    countBadge: 'bg-emerald-700 text-white',
    cardAccent: 'border-l-4 border-l-emerald-500',
    pillBg: 'bg-emerald-50 text-emerald-800 border border-emerald-200',
  },
  {
    value: 'MANUAL_STAFF', label: 'Manual', icon: UserCog,
    activeBg: 'bg-blue-600 text-white border-blue-600',
    inactiveBg: 'bg-blue-50 text-blue-800 border-blue-200 hover:bg-blue-100',
    countBadgeOnActive: 'bg-white/20 text-white',
    countBadge: 'bg-blue-700 text-white',
    cardAccent: 'border-l-4 border-l-blue-500',
    pillBg: 'bg-blue-50 text-blue-800 border border-blue-200',
  },
  {
    value: 'STAFF_BLOCK', label: 'Court Block', icon: Lock,
    activeBg: 'bg-slate-800 text-white border-slate-800',
    inactiveBg: 'bg-slate-100 text-slate-800 border-slate-300 hover:bg-slate-200',
    countBadgeOnActive: 'bg-white/20 text-white',
    countBadge: 'bg-slate-900 text-white',
    cardAccent: 'border-l-4 border-l-slate-700',
    pillBg: 'bg-slate-200 text-slate-800 border border-slate-300',
  },
  {
    value: 'WAITLIST_CONVERSION', label: 'Waitlist → Booking', icon: ListOrdered,
    activeBg: 'bg-amber-500 text-white border-amber-500',
    inactiveBg: 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100',
    countBadgeOnActive: 'bg-white/20 text-white',
    countBadge: 'bg-amber-600 text-white',
    cardAccent: 'border-l-4 border-l-amber-400',
    pillBg: 'bg-amber-50 text-amber-800 border border-amber-200',
  },
];

export const getSourceConfig = (sourceKey) =>
  SOURCE_CHIPS.find(c => c.value === sourceKey) || SOURCE_CHIPS[0];

// ---------- block label / time helpers ----------
export const formatBlockLabel = (raw) => {
  if (!raw) return 'Block';
  return raw.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, ch => ch.toUpperCase());
};

// End time for multi-hour bookings.
export const getEndTime = (startSlot, duration) => {
  const startTime = startSlot.split('-')[0];
  const [hours, minutes] = startTime.split(':').map(Number);
  let endHour = hours + duration;
  if (endHour >= 24) endHour = 0;
  return `${endHour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

// ---------- Brunei timezone date helpers ----------
// Re-export from the shared lib so callers in this module use the same
// helpers as the rest of the app.
export {
  bruneiToday as bruneiTodayISO,
  bruneiPretty as bruneiTodayPretty,
} from '../../../lib/brunei';

import { bruneiDateOffset, bruneiPretty as _bruneiPretty } from '../../../lib/brunei';

export const bruneiTomorrowISO = () => bruneiDateOffset(1);

export const bruneiTomorrowPretty = () => {
  // Build a Date at local midnight using the Brunei date string so the
  // formatter prints the same calendar day Brunei sees.
  const iso = bruneiTomorrowISO();
  return _bruneiPretty(new Date(`${iso}T12:00:00+08:00`));
};
